import React from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  Stepper,
  Step,
  StepLabel,
  Switch,
  TextField,
  Divider,
  Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/* ---------------- STEPS ---------------- */
const steps = [
  { label: "Validity Period", sub: "Financial year" },
  { label: "Calculation Rules", sub: "Define slabs" },
  { label: "Eligibility", sub: "Optional constraints" },
  { label: "CP Sign-Off", sub: "Configure window" },
  { label: "Exclusions", sub: "POP projects" },
  { label: "Review", sub: "Preview & save" },
];

export default function AOPEligibilityConstraints({
  data,
  setData,
  onBack,
  onNext,
}) {
  const eligibility = data.eligibility || {
    siteVisitEnabled: false,
    siteVisitMin: "",
    siteVisitMax: "",
    fosEnabled: false,
    fosMin: "",
    fosMax: "",
  };

  const updateEligibility = (field, value) => {
    setData({
      ...data,
      eligibility: {
        ...eligibility,
        [field]: value,
      },
    });
  };

  return (
    <Box sx={{ p: 1, minHeight: "100vh" }}>
  

{/* Back */}
<Typography
  variant="body2"
  sx={{
    mb: 3,
    cursor: "pointer",
    color: "text.secondary",
    fontSize: 14,
    fontWeight: 500,
  }}
  onClick={onBack}
>
  ← Back to AOPs
</Typography>

{/* Stepper */}
<Stepper
  activeStep={2}
  alternativeLabel
  sx={{
    mb: 6,
    "& .MuiStepLabel-label": {
      fontSize: "0.95rem",
      fontWeight: 500,
    },
    "& .MuiStepIcon-root": {
      fontSize: 32,
    },
    "& .Mui-active": {
      color: "#2563EB",
    },
    "& .Mui-completed": {
      color: "#2563EB",
    },
  }}
>
  {steps.map((step) => (
    <Step key={step.label}>
      <StepLabel>
        <Box textAlign="center">
          <Typography
            fontSize="0.95rem"
            fontWeight={600}
          >
            {step.label}
          </Typography>
          <Typography
            fontSize="0.75rem"
            color="text.secondary"
          >
            {step.sub}
          </Typography>
        </Box>
      </StepLabel>
    </Step>
  ))}
</Stepper>

      {/* Info */}
      <Box
        sx={{
          background: "#EFF6FF",
          border: "1px solid #BFDBFE",
          borderRadius: 2,
          p: 2,
          display: "flex",
          gap: 1.5,
          mb: 3,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#2563EB" }} />
        <Typography fontSize={13} color="#1E3A8A">
          <b>AOP Achievement Logic</b>
          <br />
          AOP achievement is calculated only on successful agreements.
        </Typography>
      </Box>

      {/* Eligibility Card */}
      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          boxShadow: "none",
        }}
      >
        <Typography fontSize={15} fontWeight={600} mb={0.5}>
          CP Eligibility Constraints
          <Chip label="Optional" size="small" sx={{ ml: 1 }} />
        </Typography>

        <Typography fontSize={12} color="#6B7280" mb={2}>
          These conditions ensure incentives apply to actively engaged CPs.
        </Typography>

        {/* Site Visit Constraint */}
        <ConstraintBlock
          title="Site Visit Constraint"
          desc="Ensure CPs are actively generating site visits"
          enabled={eligibility.siteVisitEnabled}
          min={eligibility.siteVisitMin}
          max={eligibility.siteVisitMax}
          onToggle={(v) => updateEligibility("siteVisitEnabled", v)}
          onMinChange={(v) => updateEligibility("siteVisitMin", v)}
          onMaxChange={(v) => updateEligibility("siteVisitMax", v)}
        />

        {/* FOS Constraint */}
        <ConstraintBlock
          title="FOS (CP Executive) Count Constraint"
          desc="Reward CPs investing in on-ground sales via FOS executives"
          enabled={eligibility.fosEnabled}
          min={eligibility.fosMin}
          max={eligibility.fosMax}
          onToggle={(v) => updateEligibility("fosEnabled", v)}
          onMinChange={(v) => updateEligibility("fosMin", v)}
          onMaxChange={(v) => updateEligibility("fosMax", v)}
        />

        {/* Warning */}
        <Box
          sx={{
            mt: 2,
            background: "#FFF7ED",
            border: "1px solid #FED7AA",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography fontSize={12} color="#9A3412">
            Additional brokerage applies only when all enabled constraints are
            satisfied.
          </Typography>
        </Box>
      </Card>

      {/* Footer */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={onNext}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

/* ---------------- CONSTRAINT BLOCK ---------------- */

function ConstraintBlock({
  title,
  desc,
  enabled,
  min,
  max,
  onToggle,
  onMinChange,
  onMaxChange,
}) {
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 2,
        p: 2,
        mb: 2,
        background: "#F9FAFB",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography fontSize={14} fontWeight={600}>
            {title}
          </Typography>
          <Typography fontSize={12} color="#6B7280">
            {desc}
          </Typography>
        </Box>
        <Switch checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
      </Box>

      {enabled && (
        <Box display="flex" gap={2} mt={2}>
          <TextField
            size="small"
            fullWidth
            label="Minimum"
            value={min}
            onChange={(e) => onMinChange(e.target.value)}
          />
          <TextField
            size="small"
            fullWidth
            label="Maximum (optional)"
            value={max}
            onChange={(e) => onMaxChange(e.target.value)}
          />
        </Box>
      )}
    </Box>
  );
}
